/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Autocomplete,
  Chip,
  CircularProgress,
  IconButton,
  Typography
} from '@mui/material';
import { Clear, LockOpen, Warning } from '@mui/icons-material';
import { CorporationByModule, CorporationUserByModule } from '../interfaces/auth.interface';
import { get10CorporationsByModule, get10CorporationsUsersByModule, getToken10CorporationsUsersByModule } from '../api/authApi';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import MySnackbar from '../components/MySnackbar';
import { setAuthData } from '../store/slices/auth';


const SupportLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {companyselected} = useSelector((state: RootState) => state.auth);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState<CorporationByModule | null>(null);
  const [selectedUser, setSelectedUser] = useState<CorporationUserByModule | null>(null);
  const [companies, setCompanies] = useState<CorporationByModule[]>([]);
  const [users, setUsers] = useState<CorporationUserByModule[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingToken, setLoadingToken] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const storedCompany = localStorage.getItem('companySelected');
    if (!storedCompany) {
      console.log('******** invalid company data')
      navigate('/ingresar');
      return;
    }

    try {
      const companyData = JSON.parse(storedCompany);
      if (companyData.Roles.Id !== 49) {
        console.log('******** invalid company role')
        navigate('/');
      }
    } catch (error) {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const response = await get10CorporationsByModule();
        console.log('get10CorporationsByModule', response)
        if (response) {
          setCompanies(response);
        }
      } catch (error) {
        setErrorMsg('Error fetching companies:' + String(error));
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
          const response = await get10CorporationsUsersByModule(selectedCompany.SecurityCorporationModuleId);
          console.log('get10CorporationsUsersByModule', response)
          if (response) {
            setUsers(response);
          }
          setActiveStep(1);
        } catch (error) {
          setErrorMsg('Error fetching users:' + String(error));
        } finally {
          setLoadingUsers(false);
        }
      };

      fetchUsers();
    }
  }, [selectedCompany]);

  const handleCompanySelect = (event: React.SyntheticEvent, value: CorporationByModule | null) => {
    setSelectedCompany(value);
    setSelectedUser(null);
  };

  const handleUserSelect = (event: React.SyntheticEvent, value: CorporationUserByModule | null) => {
    setSelectedUser(value);
    if (value) {
      setActiveStep(2);
    }
  };

  const resetForm = () => {
    setSelectedCompany(null);
    setSelectedUser(null);
    setActiveStep(0);
  };

  const handleLogin = async () => {
    if (!selectedCompany || !selectedUser || !companyselected?.Token) return;

    setLoadingToken(true);
    try {
      const response = await getToken10CorporationsUsersByModule(
        companyselected.Token,
        selectedUser.UserName,
        selectedUser.Password
      );
      console.log('getToken10CorporationsUsersByModule', response?.Value)
      if (response?.Type === 0) {
        await localStorage.clear();
        await localStorage.removeItem('connectionId');
        await localStorage.removeItem('companySelected');
        await localStorage.removeItem('userMenu');
        dispatch(setAuthData(response.Value));
        navigate('/ingresar');
        // window.location.reload();
        return;
      } 
      setErrorMsg('Error fetching user token:' + (response?.Message || 'unknown'));

    } catch (error) {
      setErrorMsg('Error fetching users:' + String(error));
      alert('Error durante el login');
    } finally {
      setLoadingToken(false);
    }
  };


  return (
    <Box sx={{ maxWidth: 800, margin: 'auto', p: 4 }}>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        <Step>
          <StepLabel>Seleccionar Empresa</StepLabel>
        </Step>
        <Step>
          <StepLabel>Seleccionar Usuario</StepLabel>
        </Step>
        <Step>
          <StepLabel>Confirmar</StepLabel>
        </Step>
      </Stepper>

      {activeStep === 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Paso 1: Seleccione una empresa
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Autocomplete
              fullWidth
              options={companies}
              getOptionLabel={(option) => `${option.Name} (RUC: ${option.RUC})`}
              loading={loadingCompanies}
              value={selectedCompany}
              onChange={handleCompanySelect}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar empresa"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCompanies ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {selectedCompany && (
              <IconButton onClick={resetForm} color="error">
                <Clear />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      {activeStep === 1 && selectedCompany && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Paso 2: Seleccione un usuario de {selectedCompany.Name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Autocomplete
              fullWidth
              options={users}
              getOptionLabel={(option) => `${option.Name} (${option.UserName})`}
              loading={loadingUsers}
              value={selectedUser}
              onChange={handleUserSelect}
              renderOption={(props, option) => (
                <li {...props} key={option.Id} >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!option.IsActive && <Warning color="error" />}
                    {option.Name} ({option.UserName})
                    {!option.IsActive && (
                      <Chip label="Inactivo" size="small" color="error" sx={{ ml: 1 }} />
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar usuario"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.Id === value.Id}
              getOptionDisabled={(option) => !option.IsActive}
            />
            {selectedUser && (
              <IconButton onClick={() => setSelectedUser(null)} color="error">
                <Clear />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      {activeStep === 2 && selectedCompany && selectedUser && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Paso 3: Confirmar selección
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Typography>
              <strong>Empresa:</strong> {selectedCompany.Name} (RUC: {selectedCompany.RUC})
            </Typography>
            <Typography>
              <strong>Usuario:</strong> {selectedUser.Name} ({selectedUser.UserName})
            </Typography>
            <Typography color={selectedUser.IsActive ? 'success.main' : 'error.main'}>
              <strong>Estado:</strong> {selectedUser.IsActive ? 'Activo' : 'Inactivo'}
            </Typography>
            <Typography>
              <strong>Rol:</strong> {selectedUser.RoleName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setActiveStep(1)}
              startIcon={<Clear />}
            >
              Cambiar Usuario
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleLogin}
              disabled={loadingToken || !selectedUser.IsActive}
              startIcon={loadingToken ? <CircularProgress size={20} /> : <LockOpen />}
            >
              {loadingToken ? 'Procesando...' : 'Ingresar'}
            </Button>
          </Box>
        </Box>
      )}

      {activeStep < 2 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={resetForm}
            sx={{ mr: 2 }}
            startIcon={<Clear />}
          >
            Reiniciar
          </Button>
          <Button
            variant="contained"
            onClick={() => setActiveStep(activeStep + 1)}
            disabled={
              (activeStep === 0 && !selectedCompany) ||
              (activeStep === 1 && !selectedUser)
            }
          >
            Siguiente
          </Button>
        </Box>
      )}
      <MySnackbar
        open={!!errorMsg}
        msg={errorMsg}
        onClose={() => setErrorMsg("")}
        severity='error'
      />
    </Box>
  );
};

export default SupportLogin;